
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Close,
  Subscriptions,
  AttachMoney,
} from '@mui/icons-material';
import { THEME_COLORS, SUBSCRIPTION_STATUS, SUBSCRIPTION_PLANS, PAYMENT_METHODS } from '../../constants';
import SearchBar from '../common/SearchBar';
import { searchContactsPaginated } from '../../services/contactService';
import { processContactsData } from '../../utils/contactUtils';
import subscriptionService from '../../services/subscriptionService';

const initialFormData = {
  contact_id: '',
  plan: SUBSCRIPTION_PLANS.MONTHLY,
  fecha_inicio: new Date().toISOString().split('T')[0],
  fecha_fin: '',
  estado: SUBSCRIPTION_STATUS.ACTIVE,
  metodo_pago: '',
  monto: '',
  notas: '',
  payer_contact_id: '',
  is_payer: true,
};

// Componente simplificado para búsqueda de pagadores
const PayerSearch = memo(({ 
  searchInput, 
  onSearchInputChange, 
  onSearch, 
  onClear, 
  searchResults, 
  searching, 
  onSelectPayer, 
  selectedPayer, 
  onClearPayer,
  error 
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ mb: 1, color: THEME_COLORS.TEXT_SECONDARY }}>
      Buscar Pagador
    </Typography>

    <SearchBar
      searchInput={searchInput}
      onSearchInputChange={onSearchInputChange}
      onSearch={onSearch}
      onClear={onClear}
      placeholder="Buscar pagador por nombre o celular..."
      onKeyPress={(e) => e.key === 'Enter' && onSearch()}
    />

    {searchResults.length > 0 && (
      <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto', borderRadius: '12px' }}>
        <List disablePadding>
          {searchResults.map(payer => (
            <ListItemButton key={payer.contact_id} onClick={() => onSelectPayer(payer)}>
              <ListItemText
                primary={`${payer.nombre} - ${payer.celular}`}
                secondary={`Plan: ${payer.plan} - Monto: $${payer.monto}`}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>
    )}

    {selectedPayer && (
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: '12px', backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Pagador: <strong>{selectedPayer.nombre}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
              {selectedPayer.celular} - Plan: {selectedPayer.plan} - Monto: ${selectedPayer.monto}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={onClearPayer}>
            Cambiar
          </Button>
        </Box>
      </Paper>
    )}

    {error && (
      <Typography variant="body2" sx={{ pl: 1, mt: 1, color: THEME_COLORS.ERROR }}>
        {error}
      </Typography>
    )}
  </Box>
));

const SubscriptionFormModal = ({ open, onClose, onSubmit, entity, isEditing, availableContacts = [], preselectedContact = null }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  
  // Estados de búsqueda simplificados
  const [contactSearch, setContactSearch] = useState('');
  const [payerSearch, setPayerSearch] = useState('');
  const [payerResults, setPayerResults] = useState([]);
  const [searchingPayers, setSearchingPayers] = useState(false);

  // Cargar pagadores potenciales - CORREGIDO
  const loadPotentialPayers = useCallback(async () => {
    try {
      // Usar el servicio que sí existe
      const response = await subscriptionService.getPotentialPayers();
      if (response.success && response.data) {
        setPayerResults(response.data);
      }
    } catch (error) {
      console.error('Error cargando pagadores:', error);
    }
  }, []);

  // Inicializar formulario - SIN cargar pagadores automáticamente
  useEffect(() => {
    if (open) {
      if (isEditing && entity) {
        setFormData({
          ...entity,
          fecha_inicio: entity.fecha_inicio?.split('T')[0] || '',
          fecha_fin: entity.fecha_fin?.split('T')[0] || '',
          monto: entity.monto?.toString() || '',
        });
        
        if (entity.contact_name) {
          setSelectedContact({ 
            contact_id: entity.contact_id, 
            nombre: entity.contact_name,
            celular: entity.contact_phone 
          });
        }

        // Restaurar pagador si existe
        if (entity.payer_contact_id && !entity.is_payer) {
          const payer = {
            contact_id: entity.payer_contact_id,
            nombre: entity.payer_name || 'Pagador',
            celular: entity.payer_phone || '',
            plan: entity.payer_plan || '',
            monto: entity.payer_monto || 0
          };
          setSelectedPayer(payer);
        }
      } else {
        setFormData({ ...initialFormData });
        if (preselectedContact) {
          setFormData(prev => ({ ...prev, contact_id: preselectedContact.contact_id }));
          setSelectedContact(preselectedContact);
        }
      }
      
      // NO cargar pagadores automáticamente - solo limpiar resultados
      setPayerResults([]);
      setPayerSearch('');
      setErrors({});
      setSubmitError('');
    }
  }, [open, isEditing, entity, preselectedContact]);

  // Manejar cambios en el formulario - LÓGICA COMPLETA RESTAURADA
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Lógica completa de pagador
      if (field === 'is_payer') {
        if (value) {
          // Si marca que él mismo paga
          newData.payer_contact_id = '';
          setSelectedPayer(null);
          setPayerSearch('');
          // Mantener el monto actual o poner un valor por defecto
          if (!newData.monto || newData.monto === '0') {
            newData.monto = '100'; // Valor por defecto
          }
        } else {
          // Si marca que otro paga
          newData.monto = '0';
          // No limpiar payer_contact_id aquí, puede que ya tenga uno seleccionado
        }
      }
      
      if (field === 'payer_contact_id') {
        if (value) {
          // Si selecciona un pagador externo
          newData.is_payer = false;
          newData.monto = '0';
        } else {
          // Si limpia el pagador externo
          newData.is_payer = true;
          setSelectedPayer(null);
          if (newData.monto === '0') {
            newData.monto = '100'; // Valor por defecto
          }
        }
      }
      
      return newData;
    });
  }, []);

  // Validación completa
  const validate = useCallback(() => {
    const newErrors = {};
    
    if (!formData.contact_id) newErrors.contact_id = 'Seleccione un contacto';
    if (!formData.plan) newErrors.plan = 'Seleccione un plan';
    if (!formData.fecha_inicio) newErrors.fecha_inicio = 'Fecha de inicio requerida';
    if (!formData.fecha_fin) newErrors.fecha_fin = 'Fecha de fin requerida';
    
    // Validar fechas
    if (formData.fecha_inicio && formData.fecha_fin) {
      if (new Date(formData.fecha_fin) <= new Date(formData.fecha_inicio)) {
        newErrors.fecha_fin = 'La fecha de fin debe ser posterior al inicio';
      }
    }
    
    // Validar lógica de pagador
    if (formData.is_payer) {
      // Si es pagador propio, debe tener monto > 0
      if (!formData.monto || Number(formData.monto) <= 0) {
        newErrors.monto = 'El monto debe ser mayor a $0 cuando paga él mismo';
      }
    } else {
      // Si no es pagador propio, debe tener un pagador seleccionado
      if (!formData.payer_contact_id) {
        newErrors.payer_contact_id = 'Seleccione quién pagará por esta suscripción';
      }
      // Y el monto debe ser 0
      if (formData.monto && Number(formData.monto) > 0) {
        newErrors.monto = 'El monto debe ser $0 cuando otro contacto paga';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Buscar pagadores - USAR DATOS DIRECTOS DEL BACKEND
  const handlePayerSearch = useCallback(async () => {
    if (!payerSearch.trim()) {
      setPayerResults([]);
      return;
    }
    
    setSearchingPayers(true);
    try {
      const contactsResponse = await searchContactsPaginated(payerSearch.trim(), 1, 20);
      
      if (contactsResponse.contacts) {
        console.log('Datos crudos del backend:', contactsResponse.contacts); // DEBUG
        
        // NO usar processContactsData, usar datos directos
        const potentialPayers = contactsResponse.contacts
          .filter(contact => contact.has_active_subscription === 1) // Campo directo del backend
          .map(contact => ({
            contact_id: contact.contact_id,
            nombre: contact.nombre,
            celular: contact.celular,
            plan: contact.subscription_plan || contact.plan || 'mensual',
            monto: contact.subscription_monto || contact.monto || 0
          }));
        
        console.log('Pagadores procesados:', potentialPayers); // DEBUG
        setPayerResults(potentialPayers);
      } else {
        setPayerResults([]);
      }
    } catch (error) {
      console.error('Error buscando pagadores:', error);
      setPayerResults([]);
    } finally {
      setSearchingPayers(false);
    }
  }, [payerSearch]);

  // Enviar formulario
  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    
    try {
      const submitData = {
        ...formData,
        monto: Number(formData.monto) || 0,
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      setSubmitError(error.message || 'Error al guardar la suscripción');
    }
  }, [formData, validate, onSubmit, onClose]);

  const filteredContacts = availableContacts.filter(contact =>
    contact.nombre.toLowerCase().includes(contactSearch.toLowerCase()) ||
    contact.celular?.includes(contactSearch)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Subscriptions sx={{ mr: 1, color: THEME_COLORS.PRIMARY }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditing ? 'Editar Suscripción' : 'Nueva Suscripción'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Selección de contacto */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.contact_id}>
              <InputLabel>Contacto</InputLabel>
              <Select
                value={formData.contact_id}
                onChange={(e) => {
                  handleInputChange('contact_id', e.target.value);
                  const contact = availableContacts.find(c => c.contact_id === e.target.value);
                  setSelectedContact(contact);
                }}
                label="Contacto"
              >
                {filteredContacts.map(contact => (
                  <MenuItem key={contact.contact_id} value={contact.contact_id}>
                    {contact.nombre} - {contact.celular}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Selector de quién paga */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>¿Quién paga?</InputLabel>
              <Select
                value={formData.is_payer ? 'self' : 'other'}
                onChange={(e) => handleInputChange('is_payer', e.target.value === 'self')}
                label="¿Quién paga?"
              >
                <MenuItem value="self">El mismo contacto (Pagador principal)</MenuItem>
                <MenuItem value="other">Otro contacto paga por él (Adicional)</MenuItem>
              </Select>
            </FormControl>
            {formData.is_payer && (
              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 1, display: 'block' }}>
                Este contacto pagará su propia suscripción. Debe especificar un monto mayor a $0.
              </Typography>
            )}
            {!formData.is_payer && (
              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 1, display: 'block' }}>
                Este contacto es un adicional. Otro contacto pagará por él. El monto será $0.
              </Typography>
            )}
          </Grid>

          {/* Detalles básicos */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={formData.plan}
                onChange={(e) => handleInputChange('plan', e.target.value)}
                label="Plan"
              >
                {Object.values(SUBSCRIPTION_PLANS).map(plan => (
                  <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Monto"
              type="number"
              value={formData.monto}
              onChange={(e) => handleInputChange('monto', e.target.value)}
              disabled={!formData.is_payer}
              InputProps={{
                startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>,
              }}
              error={!!errors.monto}
              helperText={errors.monto || (formData.is_payer ? 'Especifique el monto a pagar' : 'Monto automático: $0 (otro contacto paga)')}
              fullWidth
            />
          </Grid>

          {/* Fechas */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de Inicio"
              type="date"
              value={formData.fecha_inicio}
              onChange={(e) => handleInputChange('fecha_inicio', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.fecha_inicio}
              helperText={errors.fecha_inicio}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de Fin"
              type="date"
              value={formData.fecha_fin}
              onChange={(e) => handleInputChange('fecha_fin', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.fecha_fin}
              helperText={errors.fecha_fin || 'Especifique la fecha de vencimiento'}
              fullWidth
            />
          </Grid>

          {/* Estado y método de pago */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={formData.estado}
                onChange={(e) => handleInputChange('estado', e.target.value)}
                label="Estado"
              >
                {Object.values(SUBSCRIPTION_STATUS).map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Método de Pago</InputLabel>
              <Select
                value={formData.metodo_pago}
                onChange={(e) => handleInputChange('metodo_pago', e.target.value)}
                label="Método de Pago"
              >
                {Object.values(PAYMENT_METHODS).map(method => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Búsqueda de pagador - SOLO si no es pagador propio */}
          {!formData.is_payer && (
            <Grid item xs={12}>
              <PayerSearch
                searchInput={payerSearch}
                onSearchInputChange={setPayerSearch}
                onSearch={handlePayerSearch}
                onClear={() => {
                  setPayerSearch('');
                  setPayerResults([]); // Limpiar resultados al limpiar búsqueda
                }}
                searchResults={payerResults}
                searching={searchingPayers}
                onSelectPayer={(payer) => {
                  setSelectedPayer(payer);
                  handleInputChange('payer_contact_id', payer.contact_id);
                }}
                selectedPayer={selectedPayer}
                onClearPayer={() => {
                  setSelectedPayer(null);
                  handleInputChange('payer_contact_id', '');
                }}
                error={errors.payer_contact_id}
              />
            </Grid>
          )}

          {/* Notas */}
          <Grid item xs={12}>
            <TextField
              label="Notas (Opcional)"
              multiline
              rows={3}
              value={formData.notas}
              onChange={(e) => handleInputChange('notas', e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEditing ? 'Actualizar' : 'Crear'} Suscripción
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(SubscriptionFormModal); 
